<template>
  <HeaderTitle :back-func="goBack">
    <template #top>{{ t("profile.screen.title.web.label") }}</template>
    <template #bottom>{{ t("profile.screen.title.web.sublabel") }}</template>
  </HeaderTitle>
  <div class="grid md:grid-cols-2 grid-cols-1 gap-x-6 bg-bkk-50 rounded-lg p-3">
    <div class="md:col-span-2 pb-6">
      <h2 class="text-xl">
        {{ t("profile.screen.account.section.title") }}
      </h2>
    </div>
    <SettingsTile
      :label="t('profile.screen.account.section.name.tile')"
      :icon="ChangeNameIcon"
      :has-icon="true"
      :hide-border-top-mobile="true"
      @click="changeNameModalOpen = true"
    />
    <SettingsTile
      :label="t('more.delete.profile')"
      :icon="DeleteProfileIcon"
      :has-icon="true"
      :hide-border-top-mobile="true"
      @click="openDeleteProfile()"
      @keydown.enter.prevent="openDeleteProfile()"
    />
    <SettingsTile
      v-if="!userStore.isSbkUser"
      classes="border-b border-t-0 md:border-b-0"
      :label="t('more.change.password.title')"
      :icon="ChangeNameIcon"
      :has-icon="true"
      @click="changePasswordModalOpen = true"
    />
    <SettingsTile
      :label="t('profile.screen.account.section.logout.tile')"
      :icon="LogoutIcon"
      :borderless="true"
      :has-icon="true"
      @click="logout()"
      @keydown.enter.prevent="logout()"
    />
  </div>

  <div
    class="grid grid-cols-1 gap-x-6 bg-lightest-gray rounded-lg p-3 mt-6 border border-light-gray"
  >
    <div class="col-span-1 pb-6">
      <h2 class="text-xl">
        {{ t("profile.about.subtitle") }}
      </h2>
    </div>
    <SettingsTile
      :label="t('profile.screen.about.section.support.tile')"
      :icon="ContactIcon"
      :has-only-border-top="true"
      @click="toContactPage()"
      @keydown.enter.prevent="toContactPage()"
    />
    <SettingsTile
      :label="t('profile.screen.about.section.privacy.tile')"
      :icon="PrivacyIcon"
      :has-only-border-top="true"
      @click="toPrivacyPage()"
      @keydown.enter.prevent="toPrivacyPage()"
    />

    <SettingsTile
      :label="t('profile.screen.about.section.behaviour.tile')"
      :icon="BehaviourIcon"
      :has-only-border-top="true"
      :has-toggle="true"
    >
      <template #toggle>
        <ToggleElement v-model="toggle" @update:model-value="onToggleChange" />
      </template>
    </SettingsTile>

    <SettingsTile
      :label="t('profile.screen.about.section.terms.tile')"
      :icon="TermsAndImprintIcon"
      :has-only-border-top="true"
      @click="toTermsPage()"
    />

    <SettingsTile
      :label="t('profile.screen.about.section.imprint.tile')"
      :icon="TermsAndImprintIcon"
      :has-only-border-top="true"
      @click="toImprintPage()"
    />
    <div
      v-if="deploymentVersion"
      class="text-xs mt-2 mx-auto flex justify-center"
    >
      <span>{{ deploymentVersion }}</span>
    </div>
  </div>

  <DeleteSbkProfileModal
    :open="deleteSbkProfileModalOpen"
    @close="deleteSbkProfileModalOpen = false"
  />

  <DeleteProfileModal
    :open="deleteProfileModalOpen"
    @close="deleteProfileModalOpen = false"
  />

  <ChangePasswordModal
    :open="changePasswordModalOpen"
    @close="changePasswordModalOpen = false"
  />

  <ChangeNameModal
    v-if="changeNameModalOpen"
    :open="changeNameModalOpen"
    @close="changeNameModalOpen = false"
  />

  <CookieConsent
    v-if="cookieConsentOpen"
    :force-open="true"
    @on-save-settings="cookieConsentOpen = false"
  />
</template>
<script setup lang="ts">
import HeaderTitle from "../../components/v2/layout/header/HeaderTitle.vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import SettingsTile from "../../components/v2/settings/SettingsTile.vue";
import ChangeNameIcon from "../../components/v2/icon/ChangeNameIcon.vue";
import DeleteProfileIcon from "../../components/v2/icon/DeleteProfileIcon.vue";
import LogoutIcon from "../../components/v2/icon/LogoutIcon.vue";
import { useUserStore } from "../../store/v2/user";
import DeleteSbkProfileModal from "../../components/v2/sbk/DeleteSbkProfileModal.vue";
import DeleteProfileModal from "../../components/v2/settings/DeleteProfileModal.vue";
import ChangePasswordModal from "../../components/v2/settings/ChangePasswordModal.vue";
import ContactIcon from "../../components/v2/icon/ContactIcon.vue";
import PrivacyIcon from "../../components/v2/icon/PrivacyIcon.vue";
import BehaviourIcon from "../../components/v2/icon/BehaviourIcon.vue";
import TermsAndImprintIcon from "../../components/v2/icon/TermsAndImprintIcon.vue";
import CookieConsent from "../../components/v2/layout/CookieConsent.vue";
import ChangeNameModal from "../../components/v2/settings/ChangeNameModal.vue";
import ToggleElement from "../../components/v2/form/ToggleElement.vue";
import { useCookieConsent } from "../../plugins/useCookieConsent";
import { useClientStore } from "../../store/v2/client";
import { updateUser } from "../../api/v2/user";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const userStore = useUserStore();
const { appContext } = getCurrentInstance()!;
const clientStore = useClientStore();
const instance = getCurrentInstance();
const { installPlugins, uninstallPlugins } = useCookieConsent(
  instance,
  clientStore
);
const deleteSbkProfileModalOpen = ref(false);
const deleteProfileModalOpen = ref(false);
const changePasswordModalOpen = ref(false);
const changeNameModalOpen = ref(false);
const userAuthenticator = appContext.config.globalProperties.$userAuthenticator;
const cookieConsentOpen = ref(false);
const toggle = ref(!!userStore.getTrackingAcceptedAt);

function goBack() {
  router.back();
}

function openDeleteProfile() {
  if (userStore.isSbkUser) {
    deleteSbkProfileModalOpen.value = true;
  } else {
    deleteProfileModalOpen.value = true;
  }
}

function logout() {
  userAuthenticator.logout();
}

function toContactPage() {
  router.push({
    name: "contact",
    params: {
      slug: route.params.slug,
    },
  });
}

function toImprintPage() {
  router.push({
    name: "imprint",
    params: {
      slug: route.params.slug,
    },
  });
}

function toPrivacyPage() {
  router.push({
    name: "privacy",
    params: {
      slug: route.params.slug,
    },
  });
}

function toTermsPage() {
  router.push({
    name: "termsOfService",
    params: {
      slug: route.params.slug,
    },
  });
}

function onToggleChange(val: boolean) {
  toggle.value = val;
  if (toggle.value) {
    userStore.setTrackingAcceptedAt(true);
    installPlugins();
  } else {
    userStore.setTrackingAcceptedAt(null);
    uninstallPlugins();
  }

  updateUser({
    trackingAccepted: userStore.getTrackingAcceptedAt,
  });
}

const deploymentVersion = computed(() => {
  if (
    import.meta.env.VITE_APP_ENV === "dev" ||
    import.meta.env.VITE_APP_ENV === "stage"
  ) {
    return import.meta.env.VITE_BUILD_INFO ?? "no build info";
  }
  return null;
});
</script>
